/**
 * Day.js Utilities for Comments & UI Time Display
 *
 * Adds:
 * - formatDisplayDate → show date using company/user preferred format
 * - parseUiDateToISO → (optional) convert typed UI date back to 'YYYY-MM-DD'
 *
 * Timezone: Asia/Kolkata
 * Locale: en (default), hi (optional)
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';      // ⬅️ for 'Do'
import customParseFormat from 'dayjs/plugin/customParseFormat'; // ⬅️ for strict parses
//import 'dayjs/locale/hi'; // Hindi locale if needed
import 'dayjs/locale/en'; // Hindi locale if needed

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

// Config
const TIMEZONE = 'Asia/Kolkata';
const DEFAULT_FORMAT = 'MMM D, YYYY – h:mm A';
const LOCALE = 'hi'; // Change to 'en' if you prefer English
const IS_UTC = false; // ✅ true if your API sends UTC timestamps
dayjs.locale(LOCALE);

const getSafeDate = (datetime) => {
  return IS_UTC
    ? dayjs.utc(datetime).tz(TIMEZONE)
    : dayjs(datetime).tz(TIMEZONE);
};

/* ───────────────── EXISTING HELPERS ───────────────── */

export const formatTimeAgo = (datetime, fallbackFormat = DEFAULT_FORMAT) => {
  if (!datetime || !dayjs(datetime).isValid()) return 'a few seconds ago';
  const date = getSafeDate(datetime).tz(TIMEZONE);
  const now = dayjs().tz(TIMEZONE);
  const daysDiff = now.diff(date, 'day');

  if (daysDiff === 0) return 'Today at ' + date.format('h:mm A');
  if (daysDiff === 1) return 'Yesterday at ' + date.format('h:mm A');
  if (daysDiff <= 60) return date.fromNow();
  return date.format(fallbackFormat);
};

export const formatExactDate = (datetime, format = DEFAULT_FORMAT) => {
  if (!datetime || !dayjs(datetime).isValid()) return '-';
  return getSafeDate(datetime).tz(TIMEZONE).format(format);
};

/* ──────────────── NEW: DATE DISPLAY BY USER FORMAT ──────────────── */

// Read user's preferred date format from localStorage
function getUserDateFormat(fallback = 'DD-MM-YYYY') {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return fallback;
    const userData = JSON.parse(raw);
    return userData?.['time_format'] || fallback; // e.g., 'DD-MM-YYYY'
  } catch {
    return fallback;
  }
}

// UI tokens → Day.js tokens (they mostly match; keep a map for future)
function uiToDayjsFormat(ui) {
  const key = String(ui || '').trim();
  const map = {
    'DD-MM-YYYY': 'DD-MM-YYYY',
    'YYYY:MM:DD': 'YYYY:MM:DD',
    'YY:MM:DD':   'YY:MM:DD',
    'Do MMMM YYYY': 'Do MMMM YYYY', // needs advancedFormat + locale with ordinal
    'MMMM Do YYYY': 'MMMM Do YYYY',
    'DD:MM:YY':   'DD:MM:YY',
  };
  // Try case-insensitive match
  const hit = Object.keys(map).find(k => k.toUpperCase() === key.toUpperCase());
  return map[hit] || 'DD-MM-YYYY';
}

/**
 * Format any date-like input into the user's preferred UI format.
 *
 * @param {Date|string|number} input
 * @param {string} [uiFormat]  Optional override (uses localStorage.user.time_format when omitted)
 * @param {{ timeZone?: string, onError?: string }} [opts]
 */
export function formatDisplayDate(input, uiFormat, opts = {}) {
  const { timeZone = TIMEZONE, onError = '-' } = opts;

  const d = toDayjsSafe(input, timeZone);
  if (!d) return onError;

  const fmt = uiToDayjsFormat(uiFormat || getUserDateFormat());
  return d.format(fmt);
}

/**
 * Optional: parse a UI date string (typed in the chosen display format)
 * back to ISO 'YYYY-MM-DD' for API filters.
 */
export function parseUiDateToISO(text, uiFormat) {
  if (!text) return null;
  const fmt = uiToDayjsFormat(uiFormat || getUserDateFormat());

  // Handle the supported formats explicitly for reliability
  // 1) Day-first with '-' separator
  if (fmt === 'DD-MM-YYYY') {
    const d = dayjs(text, 'DD-MM-YYYY', true);
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }
  // 2) Day-first with ':' separator and 2-digit year
  if (fmt === 'DD:MM:YY') {
    const d = dayjs(text, 'DD:MM:YY', true);
    return d.isValid() ? d.year(normalize2DigitYear(d.year())).format('YYYY-MM-DD') : null;
  }
  // 3) Year-first with ':' separator
  if (fmt === 'YYYY:MM:DD') {
    const d = dayjs(text, 'YYYY:MM:DD', true);
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }
  // 4) Year-short with ':' separator
  if (fmt === 'YY:MM:DD') {
    const d = dayjs(text, 'YY:MM:DD', true);
    if (!d.isValid()) return null;
    const y = normalize2DigitYear(d.year());
    return d.year(y).format('YYYY-MM-DD');
  }
  // 5/6) Ordinal formats: try a loose parse and normalize
  if (fmt === 'Do MMMM YYYY' || fmt === 'MMMM Do YYYY') {
    const d = dayjs(text); // rely on native/locale parse
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }

  // Fallback: try generic parse
  const d = dayjs(text);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

/* ─────────────── internals ─────────────── */

function normalize2DigitYear(y) {
  // Day.js gives 2-digit years as 19xx sometimes; push to 2000–2099
  return y < 100 ? (y + 2000) : y;
}

/**
 * Parse to a Day.js object in the desired timezone.
 * Supports: Date, number (sec/ms), ISO, 'YYYY-MM-DD HH:mm:ss',
 * 'dd-mm-yyyy', 'dd/mm/yyyy'.
 */
function toDayjsSafe(input, timeZone = TIMEZONE) {
  if (input == null) return null;

  // Date instance
  if (input instanceof Date) {
    if (isNaN(input)) return null;
    return (IS_UTC ? dayjs.utc(input) : dayjs(input)).tz(timeZone);
  }

  // Number → sec or ms
  if (typeof input === 'number') {
    const ms = input > 1e12 ? input : input * 1000;
    const d = dayjs(ms);
    return d.isValid() ? (IS_UTC ? d.utc() : d).tz(timeZone) : null;
  }

  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return null;

    // Common SQL format 'YYYY-MM-DD HH:mm:ss'
    const sql = dayjs(s, 'YYYY-MM-DD HH:mm:ss', true);
    if (sql.isValid()) return (IS_UTC ? sql.utc() : sql).tz(timeZone);

    // ISO or general parse
    const iso = dayjs(s);
    if (iso.isValid()) return (IS_UTC ? iso.utc() : iso).tz(timeZone);

    // dd-mm-yyyy or dd/mm/yyyy (2- or 4-digit year)
    const dmyDash = dayjs(s, ['D-M-YYYY', 'DD-MM-YYYY', 'D-M-YY', 'DD-MM-YY'], true);
    if (dmyDash.isValid()) {
      const y = normalize2DigitYear(dmyDash.year());
      return dmyDash.year(y).tz(timeZone);
    }
    const dmySlash = dayjs(s, ['D/M/YYYY', 'DD/MM/YYYY', 'D/M/YY', 'DD/MM/YY'], true);
    if (dmySlash.isValid()) {
      const y = normalize2DigitYear(dmySlash.year());
      return dmySlash.year(y).tz(timeZone);
    }
  }

  return null;
}
